/**
 * Shared record-access selection semantics (DP-3188).
 *
 * Lifted verbatim out of `RecordAccessSelector`, which owned URL read + draft +
 * commit inline. The first-login picker needs the identical semantics, and two
 * copies of "which ids are selected, and what does committing them mean" is
 * exactly the divergence DP-3188 exists to prevent — so both consume this hook
 * and neither re-derives any of it.
 *
 * Three rules this hook owns:
 *
 * - **The URL is the render-time source of truth.** `selected` is derived from
 *   the query param on every render; storage is a write-through mirror, never a
 *   read here (hydration is the pre-shell gate's job).
 * - **All-selected is paramless in the URL and a sentinel in storage.** The
 *   param is deleted rather than filled with every id — the contract
 *   `encore-grid` reads — while storage records `__ALL__` so a value added to
 *   the dimension later is still covered.
 * - **A commit writes both, in one go.** Storage follows the URL commit at the
 *   same cadence (Q8), so no load or storage listener can observe a window
 *   where the two disagree.
 *
 * A commit also reports itself as `dimension_scope.sidebar_committed` (PRD §9)
 * — off the same normalisation the writes used, so the metric describes the
 * selection that landed. Instances that are not the sidebar opt out; see
 * `emitSidebarCommits`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { AccessDimensionOption } from '../api/endpoints/useAccessDimensionOptions';
import type { AccessDimension } from '../api/endpoints/useCurrentUser';
import { extractAccessDimensionParam } from '../utils/accessDimensionQueryParam';
import {
  trackDimensionSidebarCommitted,
  type DimensionScopeCommitKind,
} from '../utils/dimensionScopeO11y';
import {
  ALL_DIMENSION_VALUES,
  writeDimensionScope,
  type DimensionScopeValue,
} from '../utils/dimensionScopeStorage';
import type { DimensionScopeKeyParts } from '../utils/storageKeys';

const VALUE_SEPARATOR = ',';

export interface UseDimensionSelectionParams {
  /** Curated rows from `useAccessDimensionOptions` — the actor's whole reach. */
  options: readonly AccessDimensionOption[];
  /**
   * `useCurrentUser().activeDimension`. Nullable because consumers call this
   * hook above their own "no dimension / still loading" gate; every operation
   * degrades to a no-op until it resolves.
   */
  activeDimension: AccessDimension | null;
  clientId: string;
  /** Personnel entity id (`useCurrentUser().userId`) — see `STORAGE_KEYS`. */
  userId: string;
  /**
   * Whether a commit through this instance reports as a *sidebar* commit
   * (`dimension_scope.sidebar_committed`, PRD §9). Defaults to `true` for the
   * sidebar's own `RecordAccessSelector`; the first-login gate passes `false`,
   * because none of its writes is a sidebar interaction — hydration and the
   * forced single-value choice are not user commits at all, and the picker's
   * answer reports itself as `picker_committed`. The flag lives here rather
   * than at the call sites so the payload keeps deriving from the same
   * normalisation the commit itself uses.
   */
  emitSidebarCommits?: boolean;
}

export interface DimensionSelection {
  /** The rendered set, derived from the URL with stale ids dropped. */
  selected: Set<string>;
  /** Local draft, which may transiently hold zero-selected. */
  draftSelected: Set<string>;
  setDraft: (next: Set<string>) => void;
  /** Write `next` to the URL *and* mirror it into storage. */
  commit: (next: Set<string>) => void;
  /** Commit the all-selected state: paramless URL, `__ALL__` in storage. */
  commitAll: () => void;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const v of a) {
    if (!b.has(v)) {
      return false;
    }
  }
  return true;
}

/**
 * Reported after both writes and off the same normalisation they used, so
 * `kind` names the selection that actually landed rather than the set the
 * caller asked for. `'all'` counts the whole reach: the sentinel is a statement
 * about the dimension, not about today's rows.
 */
function reportSidebarCommit({
  dimensionName,
  clientId,
  userId,
  useSentinel,
  selectedCount,
  reachCount,
}: {
  dimensionName: string;
  clientId: string;
  userId: string;
  useSentinel: boolean;
  selectedCount: number;
  reachCount: number;
}) {
  let kind: DimensionScopeCommitKind;
  if (useSentinel) {
    kind = 'all';
  } else if (selectedCount === 0) {
    kind = 'clear';
  } else {
    kind = 'partial';
  }

  trackDimensionSidebarCommitted({
    dimensionName,
    clientId,
    userId,
    kind,
    size: kind === 'all' ? reachCount : selectedCount,
  });
}

export function useDimensionSelection({
  options,
  activeDimension,
  clientId,
  userId,
  emitSidebarCommits = true,
}: UseDimensionSelectionParams): DimensionSelection {
  const [searchParams, setSearchParams] = useSearchParams();

  // Empty until `/me` resolves, and empty for good when enforcement is
  // disabled (`dimensions: []`). Both leave the param key unknown, so both
  // resolve to "all" and commit nothing to the URL.
  const dimensionName = activeDimension?.name ?? '';

  /**
   * The canonical selected set. Missing param → every option. Stale ids
   * (values deleted server-side) drop out. If the URL ends up contributing
   * nothing, recover by selecting all so zero-selected is never what renders.
   *
   * Reads through `extractAccessDimensionParam`, the sanctioned reader, rather
   * than `searchParams.get`: a param that fails its tamper guard is treated as
   * absent, so a hand-edited value cannot smuggle half a selection past the
   * validator by pairing valid ids with invalid characters.
   */
  const selected = useMemo<Set<string>>(() => {
    const everyOption = new Set(options.map(o => o.id));
    const raw = dimensionName
      ? extractAccessDimensionParam({ url: searchParams, dimensionName })
      : null;
    if (raw === null) {
      return everyOption;
    }
    const ids = raw.split(VALUE_SEPARATOR).filter(Boolean);
    const filtered = new Set(ids.filter(id => everyOption.has(id)));
    return filtered.size === 0 ? everyOption : filtered;
  }, [searchParams, options, dimensionName]);

  // Local "draft" lets the popover transiently hold zero-selected — required so
  // disco-ui's `MultiSelectPopover` can fire its shake animation + errored
  // border when the user tries to dismiss with nothing selected. Consumers hold
  // that state with `setDraft` and only reach for `commit` once it is valid.
  const [draftSelected, setDraftSelected] = useState<Set<string>>(selected);

  // Keep the draft in lock-step with URL changes from outside the popover
  // (browser back/forward, programmatic navigation). The ref tracks the last
  // URL we mirrored so we don't clobber an in-progress empty draft when the URL
  // hasn't actually changed — a commit updates it, so a commit never echoes
  // back through here.
  const lastUrlRef = useRef(selected);
  useEffect(() => {
    if (!setsEqual(selected, lastUrlRef.current)) {
      lastUrlRef.current = selected;
      setDraftSelected(selected);
    }
  }, [selected]);

  /**
   * All three key parts, or `null` while any is still unresolved. Writing a key
   * with an empty part would collapse distinct users onto one entry, which is
   * the cross-bleed the key shape exists to prevent — so the URL still commits
   * and the storage mirror is simply skipped (it is best-effort by design; the
   * same thing happens when `localStorage` is unavailable).
   */
  const storageParts = useMemo<DimensionScopeKeyParts | null>(() => {
    if (!clientId || !userId || !dimensionName) {
      return null;
    }
    return { clientId, userId, dimensionName };
  }, [clientId, userId, dimensionName]);

  const commitSelection = useCallback(
    (next: Set<string>, allSelected: boolean) => {
      // No dimension means no param key and no storage key: there is nothing
      // selectable yet, so a commit has nowhere to land. Bailing keeps a
      // pre-resolution call from writing an empty-keyed `?=id` param.
      if (!dimensionName) {
        return;
      }
      setDraftSelected(next);

      const ids = Array.from(next).sort((a, b) => a.localeCompare(b));
      // Every id selected, or none at all, means there is nothing to write:
      // downstream reads an absent param as "all", which is also the only
      // sensible reading of a URL that names no value.
      const paramless =
        allSelected || next.size === 0 || next.size === options.length;

      const nextParams = new URLSearchParams(searchParams);
      if (paramless) {
        nextParams.delete(dimensionName);
      } else {
        nextParams.set(dimensionName, ids.join(VALUE_SEPARATOR));
      }
      setSearchParams(nextParams, { replace: true });
      lastUrlRef.current = next;

      // `__ALL__` stands in for a full selection so a value added later is
      // still covered; an empty `values` array is not written at all —
      // `writeDimensionScope` clears the entry, keeping "nothing selected"
      // indistinguishable from "never chose" for the first-login gate.
      const useSentinel =
        allSelected || (next.size > 0 && next.size === options.length);

      if (storageParts) {
        const value: DimensionScopeValue = useSentinel
          ? { v: 1, values: ALL_DIMENSION_VALUES }
          : { v: 1, values: ids };
        writeDimensionScope({ ...storageParts, value });
      }

      if (!emitSidebarCommits) {
        return;
      }
      reportSidebarCommit({
        dimensionName,
        clientId,
        userId,
        useSentinel,
        selectedCount: next.size,
        reachCount: options.length,
      });
    },
    [
      options.length,
      dimensionName,
      searchParams,
      setSearchParams,
      storageParts,
      emitSidebarCommits,
      clientId,
      userId,
    ]
  );

  const commit = useCallback(
    (next: Set<string>) => commitSelection(next, false),
    [commitSelection]
  );

  // Deliberately not `commit(everyOption)`: the sentinel is the point of this
  // path, and it holds even for an empty option list — "all" is a statement
  // about the dimension, not a snapshot of the rows loaded right now.
  const commitAll = useCallback(
    () => commitSelection(new Set(options.map(o => o.id)), true),
    [commitSelection, options]
  );

  return {
    selected,
    draftSelected,
    setDraft: setDraftSelected,
    commit,
    commitAll,
  };
}
